// Enterprise Global State Management
// Zustand store with cross-feature integration, persistence, and real-time sync

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { applyThemeToDocument, resolveTheme } from '@/theme/theme';
import type { Theme } from '@/types';

// Types
interface Tab {
  id: string;
  title: string;
  url: string;
  favicon?: string;
  groupId?: string;
  windowId?: string;
  isPinned: boolean;
  isSleeping: boolean;
  lastAccessedAt: Date;
  tags: string[];
}

interface TabGroup {
  id: string;
  name: string;
  color: string;
  icon?: string;
  tabs: string[];
  isExpanded: boolean;
  createdAt: Date;
}

interface Workspace {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  tabs: string[];
  collaborators: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface Team {
  id: string;
  name: string;
  members: TeamMember[];
  workspaces: string[];
  settings: TeamSettings;
  createdAt: Date;
}

interface TeamMember {
  userId: string;
  displayName: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  joinedAt: Date;
}

interface TeamSettings {
  allowGuestAccess: boolean;
  requireApproval: boolean;
  maxMembers: number;
}

interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  targetType: 'tab' | 'group' | 'workspace' | 'team';
  targetId: string;
  metadata?: Record<string, any>;
  timestamp: Date;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  timestamp: Date;
  actionUrl?: string;
}

interface EnterpriseSettings {
  darkMode: boolean;
  autoSave: boolean;
  realtimeSync: boolean;
  aiSuggestions: boolean;
  proactiveMode: boolean;
  performanceMonitoring: boolean;
}

// Store interface
interface AppState {
  // Tabs
  tabs: Record<string, Tab>;
  tabGroups: Record<string, TabGroup>;
  activeTabId: string | null;
  
  // Workspaces
  workspaces: Record<string, Workspace>;
  activeWorkspaceId: string | null;
  
  // Teams
  teams: Record<string, Team>;
  activeTeamId: string | null;
  
  // UI State
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  language: string;
  
  // Activity
  activityLog: ActivityLog[];
  notifications: Notification[];
  
  // Real-time
  onlineUsers: string[];
  isConnected: boolean;
  
  // Enterprise Settings
  enterpriseSettings: EnterpriseSettings;
  
  // Actions - Tabs
  addTab: (tab: Tab) => void;
  updateTab: (id: string, updates: Partial<Tab>) => void;
  removeTab: (id: string) => void;
  moveTabToGroup: (tabId: string, groupId: string) => void;
  setActiveTab: (id: string | null) => void;
  
  // Actions - Groups
  addGroup: (group: TabGroup) => void;
  updateGroup: (id: string, updates: Partial<TabGroup>) => void;
  removeGroup: (id: string) => void;
  toggleGroupExpanded: (id: string) => void;
  
  // Actions - Workspaces
  addWorkspace: (workspace: Workspace) => void;
  updateWorkspace: (id: string, updates: Partial<Workspace>) => void;
  removeWorkspace: (id: string) => void;
  setActiveWorkspace: (id: string | null) => void;
  
  // Actions - Teams
  addTeam: (team: Team) => void;
  updateTeam: (id: string, updates: Partial<Team>) => void;
  removeTeam: (id: string) => void;
  setActiveTeam: (id: string | null) => void;
  addTeamMember: (teamId: string, member: TeamMember) => void;
  removeTeamMember: (teamId: string, userId: string) => void;
  
  // Actions - UI
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setLanguage: (language: string) => void;
  
  // Actions - Activity
  addActivity: (activity: Omit<ActivityLog, 'id' | 'timestamp'> & { timestamp?: Date }) => void;
  clearActivity: () => void;
  
  // Actions - Notifications
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  
  // Actions - Real-time
  setOnlineUsers: (users: string[]) => void;
  setConnected: (connected: boolean) => void;
  
  // Actions - Enterprise Settings
  updateEnterpriseSettings: (updates: Partial<EnterpriseSettings>) => void;
  
  // Bulk operations
  bulkMoveTabs: (tabIds: string[], targetGroupId: string) => void;
  bulkDeleteTabs: (tabIds: string[]) => void;
  importTabs: (tabs: Tab[]) => void;
  
  // Reset
  resetStore: () => void;
}

const initialState = {
  tabs: {},
  tabGroups: {},
  activeTabId: null,
  workspaces: {},
  activeWorkspaceId: null,
  teams: {},
  activeTeamId: null,
  sidebarCollapsed: false,
  theme: 'dark' as const,
  language: 'en',
  activityLog: [],
  notifications: [],
  onlineUsers: [],
  isConnected: false,
  enterpriseSettings: {
    darkMode: true,
    autoSave: true,
    realtimeSync: true,
    aiSuggestions: true,
    proactiveMode: false,
    performanceMonitoring: true,
  },
};

export const useAppStore = create<AppState>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,
        
        // Tab actions
        addTab: (tab) => set((state) => {
          state.tabs[tab.id] = tab;
        }),
        
        updateTab: (id, updates) => set((state) => {
          if (state.tabs[id]) {
            Object.assign(state.tabs[id], updates);
          }
        }),
        
        removeTab: (id) => set((state) => {
          delete state.tabs[id];
          if (state.activeTabId === id) {
            state.activeTabId = null;
          }
          // Remove from groups
          Object.values(state.tabGroups).forEach(group => {
            const index = group.tabs.indexOf(id);
            if (index > -1) {
              group.tabs.splice(index, 1);
            }
          });
        }),
        
        moveTabToGroup: (tabId, groupId) => set((state) => {
          // Remove from current group
          Object.values(state.tabGroups).forEach(group => {
            const index = group.tabs.indexOf(tabId);
            if (index > -1) {
              group.tabs.splice(index, 1);
            }
          });
          // Add to new group
          if (state.tabGroups[groupId]) {
            state.tabGroups[groupId].tabs.push(tabId);
            state.tabs[tabId].groupId = groupId;
          }
        }),
        
        setActiveTab: (id) => set((state) => {
          state.activeTabId = id;
          if (id && state.tabs[id]) {
            state.tabs[id].lastAccessedAt = new Date();
          }
        }),
        
        // Group actions
        addGroup: (group) => set((state) => {
          state.tabGroups[group.id] = group;
        }),
        
        updateGroup: (id, updates) => set((state) => {
          if (state.tabGroups[id]) {
            Object.assign(state.tabGroups[id], updates);
          }
        }),
        
        removeGroup: (id) => set((state) => {
          const group = state.tabGroups[id];
          if (group) {
            // Move tabs out of group
            group.tabs.forEach(tabId => {
              if (state.tabs[tabId]) {
                state.tabs[tabId].groupId = undefined;
              }
            });
          }
          delete state.tabGroups[id];
        }),
        
        toggleGroupExpanded: (id) => set((state) => {
          if (state.tabGroups[id]) {
            state.tabGroups[id].isExpanded = !state.tabGroups[id].isExpanded;
          }
        }),
        
        // Workspace actions
        addWorkspace: (workspace) => set((state) => {
          state.workspaces[workspace.id] = workspace;
        }),
        
        updateWorkspace: (id, updates) => set((state) => {
          if (state.workspaces[id]) {
            Object.assign(state.workspaces[id], { ...updates, updatedAt: new Date() });
          }
        }),
        
        removeWorkspace: (id) => set((state) => {
          delete state.workspaces[id];
          if (state.activeWorkspaceId === id) {
            state.activeWorkspaceId = null;
          }
        }),
        
        setActiveWorkspace: (id) => set((state) => {
          state.activeWorkspaceId = id;
        }),
        
        // Team actions
        addTeam: (team) => set((state) => {
          state.teams[team.id] = team;
        }),
        
        updateTeam: (id, updates) => set((state) => {
          if (state.teams[id]) {
            Object.assign(state.teams[id], updates);
          }
        }),
        
        removeTeam: (id) => set((state) => {
          delete state.teams[id];
          if (state.activeTeamId === id) {
            state.activeTeamId = null;
          }
        }),
        
        setActiveTeam: (id) => set((state) => {
          state.activeTeamId = id;
        }),
        
        addTeamMember: (teamId, member) => set((state) => {
          if (state.teams[teamId]) {
            state.teams[teamId].members.push(member);
          }
        }),
        
        removeTeamMember: (teamId, userId) => set((state) => {
          if (state.teams[teamId]) {
            state.teams[teamId].members = state.teams[teamId].members.filter(m => m.userId !== userId);
          }
        }),
        
        // UI actions
        toggleSidebar: () => set((state) => {
          state.sidebarCollapsed = !state.sidebarCollapsed;
        }),
        
        setTheme: (theme: Theme) => set((state) => {
          state.theme = theme;
          if (theme === 'dark' || theme === 'light') {
            state.enterpriseSettings.darkMode = theme === 'dark';
          }
          applyThemeToDocument(resolveTheme(theme));
        }),
        
        setLanguage: (language) => set((state) => {
          state.language = language;
        }),
        
        // Activity actions
        addActivity: (activity) => set((state) => {
          state.activityLog.unshift({
            ...activity,
            id: `activity-${Date.now()}`,
            timestamp: activity.timestamp || new Date(),
          });
          // Keep only last 100 activities
          if (state.activityLog.length > 100) {
            state.activityLog = state.activityLog.slice(0, 100);
          }
        }),
        
        clearActivity: () => set((state) => {
          state.activityLog = [];
        }),
        
        // Notification actions
        addNotification: (notification) => set((state) => {
          state.notifications.unshift({
            ...notification,
            id: `notification-${Date.now()}`,
            timestamp: new Date(),
            read: false,
          });
        }),
        
        markNotificationRead: (id) => set((state) => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification) {
            notification.read = true;
          }
        }),
        
        clearNotifications: () => set((state) => {
          state.notifications = [];
        }),
        
        // Real-time actions
        setOnlineUsers: (users) => set((state) => {
          state.onlineUsers = users;
        }),
        
        setConnected: (connected) => set((state) => {
          state.isConnected = connected;
        }),
        
        // Enterprise settings actions
        updateEnterpriseSettings: (updates) => set((state) => {
          Object.assign(state.enterpriseSettings, updates);
        }),
        
        // Bulk operations
        bulkMoveTabs: (tabIds, targetGroupId) => set((state) => {
          tabIds.forEach(tabId => {
            // Remove from current group
            Object.values(state.tabGroups).forEach(group => {
              const index = group.tabs.indexOf(tabId);
              if (index > -1) {
                group.tabs.splice(index, 1);
              }
            });
            // Add to target group
            if (state.tabGroups[targetGroupId]) {
              state.tabGroups[targetGroupId].tabs.push(tabId);
              if (state.tabs[tabId]) {
                state.tabs[tabId].groupId = targetGroupId;
              }
            }
          });
        }),
        
        bulkDeleteTabs: (tabIds) => set((state) => {
          tabIds.forEach(id => {
            delete state.tabs[id];
            // Remove from groups
            Object.values(state.tabGroups).forEach(group => {
              const index = group.tabs.indexOf(id);
              if (index > -1) {
                group.tabs.splice(index, 1);
              }
            });
          });
          if (tabIds.includes(state.activeTabId || '')) {
            state.activeTabId = null;
          }
        }),
        
        importTabs: (tabs) => set((state) => {
          tabs.forEach(tab => {
            state.tabs[tab.id] = tab;
          });
        }),
        
        // Reset
        resetStore: () => set(initialState),
      }))
    ),
    {
      name: 'khiimori-storage',
      partialize: (state) => ({
        tabs: state.tabs,
        tabGroups: state.tabGroups,
        workspaces: state.workspaces,
        teams: state.teams,
        theme: state.theme,
        language: state.language,
        sidebarCollapsed: state.sidebarCollapsed,
        enterpriseSettings: state.enterpriseSettings,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyThemeToDocument(resolveTheme(state.theme));
        }
      },
    }
  )
);

// Selectors
export const selectTabs = (state: AppState) => Object.values(state.tabs);
export const selectGroups = (state: AppState) => Object.values(state.tabGroups);
export const selectActiveTab = (state: AppState) => state.activeTabId ? state.tabs[state.activeTabId] : null;
export const selectUnreadNotifications = (state: AppState) => state.notifications.filter(n => !n.read);
export const selectOnlineUsers = (state: AppState) => state.onlineUsers;

// Cross-feature selectors
export const selectTabsByGroup = (groupId: string) => (state: AppState) => 
  Object.values(state.tabs).filter(tab => tab.groupId === groupId);

export const selectTabsByWorkspace = (workspaceId: string) => (state: AppState) => {
  const workspace = state.workspaces[workspaceId];
  if (!workspace) return [];
  return workspace.tabs.map(tabId => state.tabs[tabId]).filter(Boolean);
};

export const selectTeamMembers = (teamId: string) => (state: AppState) => 
  state.teams[teamId]?.members || [];

export const selectRecentActivity = (limit: number = 10) => (state: AppState) => 
  state.activityLog.slice(0, limit);

// Computed selectors
export const selectGroupedTabsCount = (state: AppState) => {
  const grouped = Object.values(state.tabs).filter(t => t.groupId).length;
  const total = Object.values(state.tabs).length;
  return { grouped, ungrouped: total - grouped };
};

export const selectWorkspaceStats = (workspaceId: string) => (state: AppState) => {
  const workspace = state.workspaces[workspaceId];
  if (!workspace) return { tabCount: 0, memberCount: 0, groupCount: 0 };
  
  return {
    tabCount: workspace.tabs.length,
    memberCount: workspace.collaborators.length,
    groupCount: Object.values(state.tabGroups).filter(g => 
      workspace.tabs.some(tabId => g.tabs.includes(tabId))
    ).length,
  };
};

// Actions for cross-feature integration
export const useCrossFeatureActions = () => {
  const addTab = useAppStore((state) => state.addTab);
  const updateTab = useAppStore((state) => state.updateTab);
  const addGroup = useAppStore((state) => state.addGroup);
  const addWorkspace = useAppStore((state) => state.addWorkspace);
  const addActivity = useAppStore((state) => state.addActivity);
  const addNotification = useAppStore((state) => state.addNotification);

  return {
    // Create tab with automatic grouping
    createTabWithAutoGroup: (tab: Tab) => {
      addTab(tab);
      addActivity({
        userId: 'current-user',
        action: 'created_tab',
        targetType: 'tab',
        targetId: tab.id,
      });
    },

    // Smart group creation based on tabs
    createSmartGroup: (name: string, tabIds: string[], color: string) => {
      const groupId = `group-${Date.now()}`;
      addGroup({
        id: groupId,
        name,
        color,
        tabs: tabIds,
        isExpanded: true,
        createdAt: new Date(),
      });
      
      tabIds.forEach(tabId => {
        updateTab(tabId, { groupId });
      });
      
      addActivity({
        userId: 'current-user',
        action: 'created_group',
        targetType: 'group',
        targetId: groupId,
        metadata: { name, tabCount: tabIds.length },
      });
    },

    // Workspace with tabs and collaboration
    createWorkspaceWithTabs: (workspace: Workspace, tabIds: string[]) => {
      addWorkspace(workspace);
      
      // Move tabs to workspace
      tabIds.forEach(tabId => {
        updateTab(tabId, { groupId: undefined }); // Clear current group
      });
      
      addActivity({
        userId: 'current-user',
        action: 'created_workspace',
        targetType: 'workspace',
        targetId: workspace.id,
        metadata: { name: workspace.name, tabCount: tabIds.length },
      });

      addNotification({
        type: 'success',
        title: 'Workspace Created',
        message: `${workspace.name} has been created with ${tabIds.length} tabs`,
      });
    },
  };
};

// React hook for store
export function useStore() {
  return useAppStore();
}

// Export types
export type { Tab, TabGroup, Workspace, Team, TeamMember, TeamSettings, ActivityLog, Notification, EnterpriseSettings };
export default useAppStore;